import { useCallback, useEffect, useMemo } from "react";
import { useClub } from "@/contexts/ClubContext";
import { useGetClientQuery } from "@/queries/useGetClientQuery";
import { useGetTicketsQuery } from "@/queries/useGetTicketsQuery";

interface Club {
  id: string;
  title: string;
}

export const useScheduleClubSelection = () => {
  const { selectedClubId, selectedClubName, setSelectedClub, clubs } = useClub();
  const { data: clientData, isLoading: isClientLoading } = useGetClientQuery();
  const { data: ticketsData, isLoading: isTicketsLoading } = useGetTicketsQuery();

  // Build a unique list of clubs available for the user, indexed by id.
  // Only include clubs that exist in /clubs — clientData.club and ticket
  // available_clubs may reference clubs from a foreign base (e.g. Padelstar
  // Yaroslavl on an Akvastar account), which 1C rejects with "Не найдена
  // структурная единица" on any club-scoped endpoint.
  const clubsById = useMemo(() => {
    const knownClubIds = new Set(clubs.map((c) => c.id));
    const map = new Map<string, Club>(clubs.map((club) => [club.id, club]));

    if (clientData?.club && knownClubIds.has(clientData.club.id)) {
      map.set(clientData.club.id, { id: clientData.club.id, title: clientData.club.name });
    }

    if (ticketsData) {
      ticketsData.forEach((ticket: { type: string; available_clubs?: Club[] }) => {
        if (ticket.type === "service") return;

        ticket.available_clubs?.forEach((club) => {
          if (club.id && knownClubIds.has(club.id)) {
            map.set(club.id, club);
          }
        });
      });
    }

    return map;
  }, [clientData, ticketsData, clubs]);

  const clubsArr = useMemo(() => Array.from(clubsById.values()), [clubsById]);

  const validClubIds = useMemo(() => new Set(clubsById.keys()), [clubsById]);

  // Validate that selectedClubId is in the allowed clubs list
  // If not, automatically fallback to the first available club
  // Wait for data to load before validating to avoid premature resets
  const isDataLoaded = !isClientLoading && !isTicketsLoading;

  useEffect(() => {
    if (!isDataLoaded || clubsArr.length === 0) return;

    if (!selectedClubId || !validClubIds.has(selectedClubId)) {
      setSelectedClub(clubsArr[0].id);
    }
  }, [clubsArr, selectedClubId, setSelectedClub, isDataLoaded, validClubIds]);

  const handleClubChange = useCallback(
    (clubId: string) => {
      setSelectedClub(clubId);
    },
    [setSelectedClub]
  );

  const userName = clientData ? `${clientData.name} ${clientData.last_name}` : "";

  // Return validated club id - only return the id if it's in the allowed list
  // While data is loading, return the stored club id to prevent UI flicker
  const validatedClubId = useMemo(() => {
    if (!selectedClubId) return null;
    if (!isDataLoaded || clubsArr.length === 0) return selectedClubId;
    return validClubIds.has(selectedClubId) ? selectedClubId : null;
  }, [selectedClubId, isDataLoaded, validClubIds, clubsArr.length]);

  // Get the club name for the validated club
  // Use selectedClubName from context as fallback while loading
  const validatedClubName = useMemo(() => {
    if (!validatedClubId) return "";
    if (!isDataLoaded) return selectedClubName;
    return clubsById.get(validatedClubId)?.title || selectedClubName;
  }, [validatedClubId, clubsById, selectedClubName, isDataLoaded]);

  return {
    idClub: validatedClubId,
    selectedClubValue: validatedClubName,
    clubsArr,
    handleClubChange,
    userName,
  };
};
